/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-04
 */

// Add third-party dependencies.
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  LocationOn as LocationOnIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import {
  useUpdateProfileMutation,
  UpdateProfileRequest,
} from '../../../features/auth';
import { sanitizeErrorMessage } from '../../../utils/security';

/**
 * Interface for user privacy settings data.
 */
interface PrivacySettingsData {
  addressVisible: boolean;
  emailVisible: boolean;
  phoneVisible: boolean;
}

/**
 * Props for the PrivacySettingsSection component.
 */
interface PrivacySettingsSectionProps {
  /** Initial user data */
  initialData: PrivacySettingsData;
  /** Whether the section is disabled */
  disabled?: boolean;
  /** Callback for successful updates */
  onUpdate?: (message: string) => void;
  /** Callback for errors */
  onError?: (error: string) => void;
}

/**
 * Interface for pending privacy changes requiring confirmation.
 */
interface PendingPrivacyChange {
  field: 'addressVisible' | 'emailVisible' | 'phoneVisible';
  value: boolean;
}

/**
 * Privacy settings section component for the Settings page.
 * Handles visibility settings for address, email, and phone with confirmation dialogs.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The privacy settings section component
 *
 * @example
 * return (
 *   <PrivacySettingsSection
 *     initialData={{
 *       addressVisible: false,
 *       emailVisible: false,
 *       phoneVisible: false
 *     }}
 *     onUpdate={(message) => setSuccessMessage(message)}
 *     onError={(error) => setErrorMessage(error)}
 *   />
 * );
 */
const PrivacySettingsSection: React.FC<PrivacySettingsSectionProps> = ({
  initialData,
  disabled = false,
  onUpdate,
  onError,
}) => {
  const { t } = useTranslation('settings');
  const [formData, setFormData] = useState<PrivacySettingsData>(initialData);
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingChange, setPendingChange] =
    useState<PendingPrivacyChange | null>(null);

  // Update form data when initial data changes
  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  /**
   * Gets the field label for display in confirmation dialog.
   */
  const getPrivacyFieldLabel = (
    field: 'addressVisible' | 'emailVisible' | 'phoneVisible'
  ): string => {
    switch (field) {
      case 'addressVisible':
        return 'address';
      case 'emailVisible':
        return 'email address';
      case 'phoneVisible':
        return 'phone number';
      default:
        return 'information';
    }
  };

  /**
   * Performs the actual privacy setting update.
   */
  const performPrivacyUpdate = async (
    field: 'addressVisible' | 'emailVisible' | 'phoneVisible',
    value: boolean
  ) => {
    try {
      // Map camelCase field names to snake_case for API
      const fieldMapping = {
        addressVisible: 'address_visible',
        emailVisible: 'email_visible',
        phoneVisible: 'phone_visible',
      };

      const updateData: Partial<UpdateProfileRequest> = {
        [fieldMapping[field]]: value,
      };
      await updateProfile(updateData as UpdateProfileRequest).unwrap();

      setFormData(prev => ({ ...prev, [field]: value }));

      if (onUpdate) {
        onUpdate(t('privacy.privacyUpdated'));
      }
    } catch (error) {
      if (onError) {
        onError(sanitizeErrorMessage(error));
      }
    }
  };

  /**
   * Handles privacy setting changes with confirmation for making data public.
   */
  const handlePrivacySettingChange = (
    field: 'addressVisible' | 'emailVisible' | 'phoneVisible',
    value: boolean
  ) => {
    // If making data public, show confirmation dialog
    if (value === true) {
      setPendingChange({ field, value });
      setConfirmDialogOpen(true);
    } else {
      // Making data private doesn't need confirmation
      performPrivacyUpdate(field, value);
    }
  };

  /**
   * Confirms the privacy setting change.
   */
  const confirmPrivacyChange = () => {
    if (pendingChange) {
      performPrivacyUpdate(pendingChange.field, pendingChange.value);
    }
    setConfirmDialogOpen(false);
    setPendingChange(null);
  };

  /**
   * Cancels the privacy setting change.
   */
  const cancelPrivacyChange = () => {
    setConfirmDialogOpen(false);
    setPendingChange(null);
  };

  return (
    <Box>
      {/* Section Header */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        {t('privacy.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('privacy.description')}
      </Typography>

      {/* Address Visibility */}
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.addressVisible}
              onChange={e =>
                handlePrivacySettingChange('addressVisible', e.target.checked)
              }
              disabled={disabled || isUpdating}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationOnIcon sx={{ mr: 1, fontSize: 20 }} />
              <Box>
                <Typography variant="body1">
                  {t('privacy.addressVisible')}
                </Typography>
              </Box>
            </Box>
          }
          sx={{ alignItems: 'flex-start', ml: 0 }}
        />
      </Box>

      {/* Email Visibility */}
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.emailVisible}
              onChange={e =>
                handlePrivacySettingChange('emailVisible', e.target.checked)
              }
              disabled={disabled || isUpdating}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EmailIcon sx={{ mr: 1, fontSize: 20 }} />
              <Box>
                <Typography variant="body1">
                  {t('privacy.emailVisible')}
                </Typography>
              </Box>
            </Box>
          }
          sx={{ alignItems: 'flex-start', ml: 0 }}
        />
      </Box>

      {/* Phone Visibility */}
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.phoneVisible}
              onChange={e =>
                handlePrivacySettingChange('phoneVisible', e.target.checked)
              }
              disabled={disabled || isUpdating}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PhoneIcon sx={{ mr: 1, fontSize: 20 }} />
              <Box>
                <Typography variant="body1">
                  {t('privacy.phoneVisible')}
                </Typography>
              </Box>
            </Box>
          }
          sx={{ alignItems: 'flex-start', ml: 0 }}
        />
      </Box>

      {/* Privacy Notice */}
      <Box
        sx={{
          p: 2,
          backgroundColor: 'info.light',
          borderRadius: 1,
          border: 1,
          borderColor: 'info.main',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <VisibilityIcon sx={{ color: 'info.main', fontSize: 20, mt: 0.25 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              Privacy Notice
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your information will only be visible to other members of studios
              you belong to. Studio owners and administrators may have access to
              additional information as needed for studio management.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={cancelPrivacyChange}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon sx={{ mr: 1, color: 'warning.main' }} />
          Confirm Privacy Change
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to make your{' '}
            {pendingChange
              ? getPrivacyFieldLabel(pendingChange.field)
              : 'information'}{' '}
            visible to other studio members?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This means other members of your studios will be able to see this
            information. You can change this setting back to private at any
            time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelPrivacyChange}>Cancel</Button>
          <Button onClick={confirmPrivacyChange} variant="contained">
            Make Visible
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrivacySettingsSection;
